// Recording in the browser: a thin MediaRecorder wrapper for narration and
// voice memos. It picks a format the browser can record AND the writing-audio
// bucket accepts, reports a live input level for the meter, and always hands
// the microphone back (tracks stopped) when the take ends or is cancelled.

/**
 * In preference order. Chrome/Firefox/Edge record Opus in WebM; Safari only
 * records MP4/AAC; some Firefox builds prefer Ogg. All three are bucket types.
 */
export const RECORDER_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm'] as const

/** Speech is clear at 64 kbps, and a 20-minute narration stays near 10 MB. */
const BITS_PER_SECOND = 64_000

/**
 * The first candidate the browser says it can record, or null when it can't
 * tell us (no isTypeSupported: let MediaRecorder use its default).
 */
export function pickRecorderMime(isTypeSupported?: (type: string) => boolean): string | null {
  const check =
    isTypeSupported ??
    (typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function'
      ? (t: string) => MediaRecorder.isTypeSupported(t)
      : null)
  if (!check) return null
  for (const type of RECORDER_MIME_TYPES) {
    try {
      if (check(type)) return type
    } catch {
      /* some engines throw on unknown codecs strings */
    }
  }
  return null
}

export type RecorderSupport = 'ok' | 'insecure' | 'unsupported'

/** Whether this page can record at all. getUserMedia only exists on https (or localhost). */
export function recorderSupport(): RecorderSupport {
  if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') return 'unsupported'
  if (!navigator.mediaDevices?.getUserMedia) return window.isSecureContext === false ? 'insecure' : 'unsupported'
  return 'ok'
}

export type RecorderErrorCode = 'blocked' | 'no-device' | 'busy' | 'insecure' | 'unsupported' | 'failed'

export class RecorderError extends Error {
  readonly code: RecorderErrorCode
  constructor(code: RecorderErrorCode, message: string) {
    super(message)
    this.name = 'RecorderError'
    this.code = code
  }
}

const MESSAGES: Record<RecorderErrorCode, string> = {
  blocked: 'Microphone access is blocked. Allow it for this site in your browser’s settings, or upload a file instead.',
  'no-device': 'No microphone was found. Plug one in, or upload a file instead.',
  busy: 'Your microphone is being used by another app. Close it and try again, or upload a file.',
  insecure: 'Recording needs a secure (https) connection. Upload a file instead.',
  unsupported: 'This browser can’t record audio here. Upload a file instead.',
  failed: 'Recording couldn’t start. Try again, or upload a file instead.',
}

export function recorderMessage(code: RecorderErrorCode): string {
  return MESSAGES[code]
}

/** getUserMedia / MediaRecorder failures, as something a person can act on. */
export function toRecorderError(e: unknown): RecorderError {
  if (e instanceof RecorderError) return e
  const name = (e as { name?: string } | null)?.name ?? ''
  const code: RecorderErrorCode =
    name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError'
      ? 'blocked'
      : name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError'
        ? 'no-device'
        : name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError'
          ? 'busy'
          : 'failed'
  return new RecorderError(code, MESSAGES[code])
}

export interface Recording {
  blob: Blob
  /** What the blob is labelled, e.g. "audio/webm;codecs=opus". */
  mimeType: string
  /** Seconds of wall-clock recording (a fallback when the file can't be decoded). */
  duration: number
}

export interface Recorder {
  readonly mimeType: string
  /** Seconds since recording started (frozen once stopped). */
  elapsed(): number
  /** Finish the take. Resolves once the last chunk is in. startRecording's takes release the mic; a Microphone's keep it. */
  stop(): Promise<Recording>
  /** Throw the take away (startRecording's takes also release the mic). */
  cancel(): void
}

export interface StartOptions {
  /** Input level 0..1, about 20 times a second, for a live meter. */
  onLevel?(level: number): void
  /** The browser stopped the take on its own (mic unplugged, permission revoked). Call stop() to keep what was recorded. */
  onInterrupt?(): void
}

/** Ask for the microphone and start recording. Throws a RecorderError with a readable message. */
export async function startRecording(opts: StartOptions = {}): Promise<Recorder> {
  const mic = await openMicrophone({ onLevel: opts.onLevel })
  let take: Recorder
  try {
    take = mic.record({ onInterrupt: opts.onInterrupt })
  } catch (e) {
    mic.release()
    throw e
  }
  return {
    get mimeType() {
      return take.mimeType
    },
    elapsed: () => take.elapsed(),
    async stop() {
      try {
        return await take.stop()
      } finally {
        mic.release()
      }
    },
    cancel() {
      take.cancel()
      mic.release()
    },
  }
}

export interface TakeOptions {
  /** The browser stopped the take on its own (mic unplugged, permission revoked). Call stop() to keep what was recorded. */
  onInterrupt?(): void
}

/**
 * An open microphone that records any number of takes without asking again:
 * narration recorded paragraph by paragraph starts a new take per piece. Its
 * takes' stop() and cancel() leave the microphone open; release() hands it
 * back (and cancels a take still running).
 */
export interface Microphone {
  /** Start a take on the open stream. Throws a RecorderError. */
  record(opts?: TakeOptions): Recorder
  /**
   * Finish `current` and start the next take straight after it (the gap is
   * the few milliseconds between two calls), for "next paragraph".
   */
  next(current: Recorder, opts?: TakeOptions): { finished: Promise<Recording>; next: Recorder }
  /** Stop the level meter and hand the microphone back. Safe to call twice. */
  release(): void
  readonly released: boolean
}

/** Ask for the microphone once, for a run of takes. Throws a RecorderError with a readable message. */
export async function openMicrophone(opts: { onLevel?(level: number): void } = {}): Promise<Microphone> {
  const support = recorderSupport()
  if (support !== 'ok') throw new RecorderError(support, MESSAGES[support])

  let stream: MediaStream
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    })
  } catch (e) {
    throw toRecorderError(e)
  }
  const meter = opts.onLevel ? startMeter(stream, opts.onLevel) : null
  const running = new Set<Recorder>()
  let released = false

  const mic: Microphone = {
    get released() {
      return released
    },
    record(takeOpts = {}) {
      if (released) throw new RecorderError('failed', MESSAGES.failed)
      const take = startTake(stream, takeOpts, () => running.delete(take))
      running.add(take)
      return take
    },
    next(current, takeOpts) {
      // stop() ends the old take synchronously, before the new one starts.
      const finished = current.stop()
      return { finished, next: mic.record(takeOpts) }
    },
    release() {
      if (released) return
      released = true
      running.forEach((t) => t.cancel())
      running.clear()
      meter?.stop()
      stream.getTracks().forEach((t) => t.stop())
    },
  }
  return mic
}

/** One MediaRecorder take on an open stream. `done` runs once it is told to stop, or cancelled. */
function startTake(stream: MediaStream, opts: TakeOptions, done: () => void): Recorder {
  const mime = pickRecorderMime()
  let rec: MediaRecorder
  try {
    rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: BITS_PER_SECOND })
  } catch {
    try {
      rec = new MediaRecorder(stream)
    } catch (e) {
      throw toRecorderError(e)
    }
  }

  const chunks: Blob[] = []
  let finished = false
  let stoppedAt: number | null = null
  let resolveStopped: () => void = () => {}
  const stopped = new Promise<void>((resolve) => (resolveStopped = resolve))
  rec.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data)
  }
  rec.onstop = () => {
    resolveStopped()
    if (!finished) {
      stoppedAt ??= performance.now()
      opts.onInterrupt?.()
    }
  }

  try {
    // A timeslice keeps long takes in small chunks instead of one huge buffer.
    rec.start(1000)
  } catch (e) {
    throw toRecorderError(e)
  }
  const startedAt = performance.now()
  const type = () => rec.mimeType || mime || chunks[0]?.type || 'audio/webm'

  return {
    get mimeType() {
      return type()
    },
    elapsed: () => ((stoppedAt ?? performance.now()) - startedAt) / 1000,
    async stop() {
      if (!finished) {
        finished = true
        // No longer "running": releasing the mic now won't throw this take away.
        done()
        stoppedAt ??= performance.now()
        if (rec.state !== 'inactive') rec.stop()
        else resolveStopped()
      }
      await stopped
      const duration = ((stoppedAt ?? performance.now()) - startedAt) / 1000
      return { blob: new Blob(chunks, { type: type() }), mimeType: type(), duration }
    },
    cancel() {
      finished = true
      rec.ondataavailable = null
      try {
        if (rec.state !== 'inactive') rec.stop()
      } catch {
        /* already stopped */
      }
      chunks.length = 0
      done()
    },
  }
}

type AudioContextCtor = typeof AudioContext

/** RMS of the mic signal through an AnalyserNode, eased so speech fills the meter. */
function startMeter(stream: MediaStream, onLevel: (level: number) => void): { stop(): void } | null {
  const Ctx: AudioContextCtor | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext
  if (!Ctx) return null
  let ctx: AudioContext
  let source: MediaStreamAudioSourceNode
  let analyser: AnalyserNode
  try {
    ctx = new Ctx()
    source = ctx.createMediaStreamSource(stream)
    analyser = ctx.createAnalyser()
    analyser.fftSize = 1024
    source.connect(analyser)
  } catch {
    return null
  }
  void ctx.resume?.().catch(() => {})
  const data = new Uint8Array(analyser.fftSize)
  let raf = 0
  let last = 0
  const tick = (now: number) => {
    raf = requestAnimationFrame(tick)
    if (now - last < 50) return
    last = now
    analyser.getByteTimeDomainData(data)
    let sum = 0
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128
      sum += v * v
    }
    // Speech RMS sits around 0.02–0.2; the square root spreads that across the meter.
    onLevel(Math.min(1, Math.sqrt(Math.sqrt(sum / data.length)) * 1.6))
  }
  raf = requestAnimationFrame(tick)
  return {
    stop() {
      cancelAnimationFrame(raf)
      try {
        source.disconnect()
      } catch {
        /* already disconnected */
      }
      void ctx.close().catch(() => {})
    },
  }
}
